import React, { useState } from 'react';
import '../css/Login.css';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import { useLoginContext } from './LoginContext';
import { domain } from './Domain';
import { motion, AnimatePresence } from 'framer-motion';
import { useActivePage } from './ActivePageContext';
import { User, Lock, Eye, EyeOff, LogIn } from 'lucide-react';

const Login = () => {
    const { setActivePage } = useActivePage();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { setIsLoggedIn, userInfo, setUserInfo } = useLoginContext();
    const [login, setLogin] = useState(false);
    const [errors, setErrors] = useState('');

    const handleLogin = (e) => {
        e.preventDefault();
        if (username === '' || password === '') {
            alert('Please set both username and password');
            return;
        }
        setLogin(true);
        const formData = new FormData();
        const requestOptions = {
            method: 'POST',
            credentials: 'omit',
            body: formData,
        };
        formData.append('username', username);
        formData.append('password', password);
        fetch(`${domain}/accounts/login`, requestOptions)
            .then((res) => {
                if (!res.ok) {
                    throw Error(res.statusText);
                }
                return res.json();
            })
            .then((data) => {
                if (data.token) {
                    setErrors('Login successful — redirecting…');
                    setLogin(false);
                    setUsername('');
                    setPassword('');
                    setIsLoggedIn(true);
                    userInfo.id = data.user_info.id;
                    userInfo.name = data.user_info.fullname;
                    userInfo.date = data.user_info.date_joined;
                    userInfo.image = data.user_info.image;
                    userInfo.role = data.user_info.role;
                    userInfo.token = data.token;
                    setUserInfo({ ...userInfo });
                    setTimeout(() => {
                        setErrors('');
                        setActivePage('dashboard');
                        navigate('/dashboard');
                    }, 2000);
                }
                if (data.non_field_errors) {
                    setErrors(data.non_field_errors[0]);
                    setLogin(false);
                }
                if (data.message) {
                    setErrors(data.message);
                    setLogin(false);
                }
            })
            .catch((err) => {
                alert(err.message);
                setLogin(false);
            });
    };

    const isLoginFormValid = username.trim() !== '' && password.trim() !== '';

    return (
        <>
            <Header />
            <div
                id="loginForm"
                className="login-page animate__animated animate__fadeIn"
            >
                <div className="login-card">
                    <form onSubmit={handleLogin} className="login-form" noValidate>
                        <div className="login-card-header">
                            <h1 className="login-title">Sign in</h1>
                            <p className="login-subtitle">
                                Industrial Parts — access your account
                            </p>
                        </div>

                        {errors !== '' && (
                            <AnimatePresence>
                                <motion.div
                                    key={errors}
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    className={`login-alert ${
                                        errors.includes('redirecting')
                                            ? 'login-alert--success'
                                            : 'login-alert--info'
                                    }`}
                                    role="status"
                                >
                                    {errors}
                                </motion.div>
                            </AnimatePresence>
                        )}

                        <div className="login-fields">
                            <label htmlFor="login-username" className="login-label">
                                Username
                            </label>
                            <div className="login-input-wrap">
                                <span className="login-input-icon" aria-hidden>
                                    <User size={18} strokeWidth={2} />
                                </span>
                                <input
                                    id="login-username"
                                    type="text"
                                    name="username"
                                    autoComplete="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Enter your username"
                                    className="login-input"
                                />
                            </div>

                            <label htmlFor="login-password" className="login-label">
                                Password
                            </label>
                            <div className="login-input-wrap login-input-wrap--password">
                                <span className="login-input-icon" aria-hidden>
                                    <Lock size={18} strokeWidth={2} />
                                </span>
                                <input
                                    id="login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="login-input login-input--with-toggle"
                                />
                                <button
                                    type="button"
                                    className="login-password-toggle"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={
                                        showPassword ? 'Hide password' : 'Show password'
                                    }
                                    tabIndex={0}
                                >
                                    {showPassword ? (
                                        <EyeOff size={18} strokeWidth={2} />
                                    ) : (
                                        <Eye size={18} strokeWidth={2} />
                                    )}
                                </button>
                            </div>

                            <div className="login-forgot-row">
                                <Link
                                    to="/forgot-password"
                                    className="login-link-muted"
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            {login ? (
                                <button
                                    type="button"
                                    disabled
                                    className="login-submit login-submit--loading"
                                >
                                    <span className="login-submit-inner">
                                        Signing you in…
                                    </span>
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    className="login-submit"
                                    disabled={!isLoginFormValid}
                                >
                                    <span className="login-submit-inner">
                                        <LogIn size={18} strokeWidth={2} />
                                        Sign in
                                    </span>
                                </button>
                            )}
                        </div>

                        <div className="login-register-hint">
                            <span className="login-register-text">
                                New to Industrial Parts?
                            </span>{' '}
                            <Link to="/register" className="login-link-primary">
                                Create an account
                            </Link>
                        </div>

                        <div className="login-footer-links">
                            <hr className="login-hr" />
                            <div className="login-legal">
                                <a href="/" className="login-link-muted">
                                    Conditions of use
                                </a>
                                <span className="login-dot" aria-hidden>
                                    ·
                                </span>
                                <a href="/" className="login-link-muted">
                                    Privacy notice
                                </a>
                            </div>
                            <p className="login-copyright">
                                © {new Date().getFullYear()} Industrial Parts Ltd
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default Login;
